import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { customerDetailAdapter, customerDetailSelectors } from 'features/customer/services/adapters/details';
import actions from './actions';
import { NAME_REDUCER } from './constants';
import {
  ICustomer,
  IResActiveCoupons,
  IResCustomers,
  IResSummaryReport,
  IResTransactions,
} from './types/api';
import { IResAppointment } from './types/appointment';
import { ICoupon } from './types/coupon';
import { IState } from './types/reducer';
import { IResReferrals } from './types/referral';

const initialState: IState = {
  isWindow: false,
  params: {
    page: 1,
    size: 10,
  },
  paramsAppointment: {
    page: 1,
    size: 10,
  },
  paramsTransactions: {
    page: 1,
    size: 10,
  },
  paramsReferral: {
    page: 1,
    size: 10,
    couponType: '',
  },
  paramsCoupon: {
    page: 1,
    size: 10,
  },
  paramsActiveCoupon: {
    page: 1,
    size: 10,
  },

  totalElement: 0,
  totalAppointmentElement: 0,
  totalTransactionsElement: 0,
  totalReferralElement: 0,
  totalActiveCouponElement: 0,

  customers: [],
  customerDetails: undefined,
  summaryReport: {
    amountSpend: 0,
    point: 0,
    totalUsedCoupon: 0,
    totalAppointment: 0,
    totalWalkIn: 0,
  },
  appointmentList: [],
  transactionList: [],
  referralList: [],
  couponList: [],
  activeCouponList: [],
  customersActive: [],
  customerSummary: null,
  popoverCustomerDetail: null,
  popoverCustomerDetailCache: customerDetailAdapter.getInitialState(),
  popoverCustomerDetailLoading: false,
};

export const Slice = createSlice({
  name: NAME_REDUCER,
  initialState,
  reducers: {
    updateCusListItem: (state, { payload }: PayloadAction<ICustomer>) => {
      state.customers = state.customers.map((item) => {
        if (item.id === payload.id) {
          return ({
            ...item,
            point: payload.point,
          });
        }
        return item;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(actions.viewCustomerDetailById.fetch, (state, { payload }) => {
        state.popoverCustomerDetailLoading = true;
        const isExist = customerDetailSelectors.selectById(state, payload);
        if (isExist) {
          state.popoverCustomerDetailLoading = false;
          state.popoverCustomerDetail = isExist;
        }
      })
      .addCase(actions.viewCustomerDetailById.success, (state, { payload }) => {
        state.popoverCustomerDetailLoading = false;
        state.popoverCustomerDetail = payload;
        customerDetailAdapter.upsertOne(state.popoverCustomerDetailCache, payload);
      })
      .addCase(actions.getCustomerSummary.success, (state, { payload }) => {
        state.customerSummary = payload;
      })

      .addCase(
        actions.getCustomers.success,
        (state, { payload }: PayloadAction<IResCustomers>) => {
          state.customers = payload.content;
          state.totalElement = payload.totalElements;
        }
      )
      .addCase(actions.setCustomerParam, (state, { payload }) => {
        const _params = {
          ...(state.params ?? {}),
          ...payload,
        };
        state.params = _params;
      })
      .addCase(actions.handleChangeDisplayType, (state) => {
        state.isWindow = !state.isWindow;
      })
      .addCase(
        actions.getCustomerDetails.success,
        (state, { payload }: PayloadAction<ICustomer>) => {
          state.customerDetails = payload;
        }
      )
      .addCase(actions.setAppointmentParams, (state, { payload }) => {
        const _params = {
          ...(state.paramsAppointment ?? {}),
          ...payload,
        };
        state.paramsAppointment = _params;
      })
      .addCase(
        actions.getAppointmentList.success,
        (state, { payload }: PayloadAction<IResAppointment>) => {
          state.appointmentList = payload.content;
          state.totalAppointmentElement = payload.totalElements;
        }
      )
      .addCase(actions.setTransactionsParams, (state, { payload }) => {
        const _params = {
          ...(state.paramsTransactions ?? {}),
          ...payload,
        };
        state.paramsTransactions = _params;
      })
      .addCase(
        actions.getTransactionsList.success,
        (state, { payload }: PayloadAction<IResTransactions>) => {
          state.transactionList = payload.content;
          state.totalTransactionsElement = payload.totalElements;
        }
      )

      .addCase(actions.setReferralsParams, (state, { payload }) => {
        const _params = {
          ...(state.paramsReferral ?? {}),
          ...payload,
        };
        state.paramsReferral = _params;
      })
      .addCase(
        actions.getReferrals.success,
        (state, { payload }: PayloadAction<IResReferrals>) => {
          state.referralList = payload.content;
          state.totalReferralElement = payload.totalElements;
        }
      )
      .addCase(actions.setCouponsParams, (state, { payload }) => {
        const _params = {
          ...(state.paramsCoupon ?? {}),
          ...payload,
        };
        state.paramsCoupon = _params;
      })
      .addCase(actions.setActiveCouponsParams, (state, { payload }) => {
        const _params = {
          ...(state.paramsActiveCoupon ?? {}),
          ...payload,
        };
        state.paramsActiveCoupon = _params;
      })
      .addCase(
        actions.getCoupons.success,
        (state, { payload }: PayloadAction<ICoupon[]>) => {
          state.couponList = payload;
        }
      )
      .addCase(
        actions.getActiveCoupons.success,
        (state, { payload }: PayloadAction<IResActiveCoupons>) => {
          state.activeCouponList = payload.content;
          state.totalActiveCouponElement = payload.totalElements;
        }
      )

      .addCase(
        actions.getSummaryReport.success,
        (state, { payload }: PayloadAction<IResSummaryReport>) => {
          state.summaryReport = payload;
        }
      )
      .addCase(
        actions.getCustomersActive.success,
        (state, { payload }: PayloadAction<any>) => {
          state.customersActive = payload.content;
        }
      )
      .addCase(actions.deleteCustomer.success, (state) => {
        state.customerDetails = undefined;
        state.paramsActiveCoupon = {
          page: 1,
          size: 10,
        };
        state.paramsAppointment = {
          page: 1,
          size: 10,
        };
        state.paramsTransactions = {
          page: 1,
          size: 10,
        };
        state.paramsReferral = {
          page: 1,
          size: 10,
          couponType: '',
        };
        state.paramsCoupon = {
          page: 1,
          size: 10,
        };
        state.totalAppointmentElement = 0;
        state.totalTransactionsElement = 0;
        state.totalReferralElement = 0;
        state.totalActiveCouponElement = 0;
        state.customerDetails = undefined;
        state.summaryReport = {
          amountSpend: 0,
          point: 0,
          totalUsedCoupon: 0,
          totalAppointment: 0,
          totalWalkIn: 0,
        };
        state.appointmentList = [];
        state.transactionList = [];
        state.referralList = [];
        state.couponList = [];
        state.activeCouponList = [];
      })
      .addCase(actions.doInit, (state) => {

        state.isWindow = false;
        state.params = {
          page: 1,
          size: 10,
        };
        state.paramsAppointment = {
          page: 1,
          size: 10,
        };
        state.paramsTransactions = {
          page: 1,
          size: 10,
        };
        state.paramsReferral = {
          page: 1,
          size: 10,
          couponType: '',
        };
        state.paramsCoupon = {
          page: 1,
          size: 10,
        };

        state.paramsActiveCoupon = {
          page: 1,
          size: 10,
        };

        state.totalElement = 0;
        state.totalAppointmentElement = 0;
        state.totalTransactionsElement = 0;
        state.totalReferralElement = 0;
        state.totalActiveCouponElement = 0;
        state.customers = [];
        state.customerDetails = undefined;
        state.summaryReport = {
          amountSpend: 0,
          point: 0,
          totalUsedCoupon: 0,
          totalAppointment: 0,
          totalWalkIn: 0,
        };
        state.appointmentList = [];
        state.transactionList = [];
        state.referralList = [];
        state.couponList = [];
        state.customersActive = [];
        state.activeCouponList = [];

      })
      ;
  },
});
export const customerUIActions = Slice.actions;
const customerServiceReducer = Slice.reducer;
export default customerServiceReducer;
